import '../../../../../plugins/hlsjs/hls.min-es5';
import ListenerManager from './listenerManager';
import MediaManager from './mediaManager';
import displayError from '../../../../../shared/actions/player/displayError';
import { IPlayerError } from '../../../../../shared/types/interfaces/error';
import { PlayerError, PlayerType } from '@shared/types/enums/player';
import { MediaPlayerProps as Props } from '../../../../../shared/types/props/player';
import { adsSelector } from '@shared/store/slices/ads.slice';
import { useAppSelector } from '@shared/store/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import AnalyticsInstance from '@shared/utils/analytics';

const ZAPPER_DELAY = 500;

export default function MediaPlayer({ drm, headers, position, source }: Props) {
  const { pauseAd } = useAppSelector(adsSelector);
  const [loaded, setLoaded] = useState(false);
  const player = useRef<Hls | null>(null);
  const video = useRef<HTMLVideoElement | null>(null);
  const autoplay = useMemo(() => !pauseAd, []);
  const props = {
    hls: player?.current,
    video: video?.current,
    source,
    ...position,
  };

  useEffect(() => {
    // Player destruction
    return () => {
      player.current?.destroy();
      player.current = null;
    };
  }, []);

  useEffect(() => {
    // Initialize default error
    const error: IPlayerError = {
      player: PlayerType.HLSJS,
      type: PlayerError.LOAD_ERROR,
    };

    // Check if hls is available
    if (!Hls.isSupported()) {
      const text = 'Hls is not available';
      error.detail = text;
      console.error('[Hls.js]', text);
      displayError(error);
      return;
    }

    let playerTimeout: ReturnType<typeof setTimeout> | undefined = undefined;

    try {
      setLoaded(false);
      playerTimeout = setTimeout(() => {
        if (source) {
          // Required headers
          const {
            accessToken,
            contentType,
            deviceId,
            planId,
            platform,
            userId,
          } = headers || {};

          // Player configuration
          const config = {
            autoplay,
            liveDurationInfinity: true,
            startPosition:
              Math.max(position.keepWatching ?? 0, position.bookmark ?? 0) ||
              position.start,
            ...(drm && {
              emeEnabled: true,
              licenseXhrSetup: (xhr: XMLHttpRequest) => {
                xhr.setRequestHeader('content-type', contentType || '');
                xhr.setRequestHeader('x-access-token', accessToken || '');
                xhr.setRequestHeader('x-tvup-device', deviceId || '');
                xhr.setRequestHeader('x-tvup-planid', planId || '');
                xhr.setRequestHeader('x-tvup-platform', platform || '');
                xhr.setRequestHeader('x-tvup-userid', userId || '');
              },
              widevineLicenseUrl: drm?.license,
            }),
          };

          // Player creation
          player.current = new Hls(config);

          // Player initialization
          if (video.current) {
            AnalyticsInstance.getOmnitrackManager()?.attachHlsJSPlayer(
              player.current,
              video.current
            );
            player.current.loadSource(source);
            player.current.attachMedia(video.current);
            setLoaded(true);
          }
        } else {
          const text = 'No source available';
          error.detail = text;
          console.error('[Hls.js]', text);
          displayError(error);
        }
      }, ZAPPER_DELAY);
    } catch (e) {
      const text = 'Error loading player';
      error.detail = text;
      console.error('[Hls.js]', text, e);
      displayError(error);
    }

    return () => {
      player.current?.loadSource('');
      clearTimeout(playerTimeout);
      setLoaded(false);
    };
  }, [source]);

  return (
    <>
      <video
        autoPlay={autoplay}
        id={'video'}
        ref={video}
        height={'100%'}
        width={'100%'}
      />
      {loaded ? (
        <>
          <ListenerManager {...props} />
          <MediaManager {...props} />
        </>
      ) : null}
    </>
  );
}
